import React, { useCallback, useEffect, useId, useState } from 'react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { cn, getCmdModifierKeySymbol } from '@cotera/client/v0/lib/utils';
import type { DatasetArtifactMetadataOutput } from '@cotera/client/app/etc/api/typed-client';
import { SqlAutocompleteInput } from './sql-autocomplete-input';

/**
 * The ad-hoc DuckDB query line at the bottom of a dataset preview.
 *
 * Drawn as a field rather than as a line of footer text: the statement that
 * sits here is `SELECT * FROM dataset` on arrival, and with no border, no
 * button and no label that is indistinguishable from a caption saying what the
 * grid is showing. So it gets the three things that say "typing happens here" —
 * a labelled, bordered box that reacts to hover and focus, and a Run button
 * that fills in when the draft no longer matches what the grid is showing.
 *
 * One signifier per idea, and no more. The label, the monospace text and the
 * placeholder already say "SQL", so there is no terminal icon; the button says
 * "Run", so there is no play icon beside the word. The ⌘-Enter shortcut lives
 * in the button's tooltip rather than on its face: printed there permanently
 * it is decoration after the first read, and it crowds out the only thing on
 * this row that carries information — whether the grid is still showing the
 * old statement.
 *
 * Save keeps the statement for everyone who opens the dataset next, as against
 * Run, which only changes what this session is looking at. It appears on the
 * row only while those two differ, so the button is either actionable or
 * absent: a permanently visible Save that is greyed out nine times in ten
 * teaches a reader nothing except to stop looking at it.
 */
export const DatasetQueryBar: React.FC<{
  /** The statement the grid is currently showing. */
  value: string;
  /** Why the last run failed, if it did. */
  error: string | null;
  /** What `dataset` resolves to: the columns the statement can name. */
  columns: DatasetArtifactMetadataOutput['schema']['columns'];
  onRun: (query: string) => void;
  /**
   * Whether the applied statement differs from the one saved on the dataset.
   * False wherever a query is not persisted at all — the row picker mounts this
   * grid outside any dataset, where a query is a way to find rows rather than
   * an edit to keep.
   */
  canSave?: boolean;
  onSave?: () => void;
}> = ({ value, error, columns, onRun, canSave = false, onSave }) => {
  const inputId = useId();
  const errorId = useId();
  const [draft, setDraft] = useState(value);
  /**
   * Whether the statement has outgrown one line. The buttons sit inline beside
   * a one-liner, which is where they read as part of the same row — but against
   * a six-line query an inline button floats in the middle of the text with
   * nothing to align to, so once the box grows they move to its bottom-right,
   * the corner an editor's actions are looked for in.
   */
  const [multiline, setMultiline] = useState(false);
  const handleLinesChange = useCallback(
    (lines: number) => setMultiline(lines > 1),
    []
  );

  useEffect(() => setDraft(value), [value]);

  // An edit the grid has not been asked to run yet. The Run button goes solid
  // on it, which is the whole point of the button being there: it is what tells
  // a reader mid-edit that the grid is still showing the old statement.
  const dirty = draft.trim() !== value.trim();
  const runnable = draft.trim() !== '';

  return (
    <form
      className="flex items-start gap-2 px-3 pb-2"
      onSubmit={(event) => {
        event.preventDefault();
        onRun(draft);
      }}
    >
      <label
        htmlFor={inputId}
        // Borders it can't see, so it stands exactly as tall as the field
        // beside it and stays centred on it once an error pushes the column
        // taller.
        className="flex h-8 shrink-0 cursor-text items-center border border-transparent text-[11px] uppercase leading-none tracking-wider text-muted-foreground"
      >
        Query
      </label>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className={cn(
            'min-w-0 rounded-md border bg-background transition-colors',
            'hover:border-ring/60 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20',
            // One line: the field and its buttons share a row. Grown: the
            // buttons go under the statement, right-aligned.
            multiline
              ? 'flex flex-col px-2.5 pb-1.5'
              : 'flex items-start gap-2 pl-2.5 pr-1',
            error !== null ? 'border-destructive/60' : 'border-border'
          )}
        >
          <SqlAutocompleteInput
            id={inputId}
            value={draft}
            columns={columns}
            placeholder="SELECT * FROM dataset WHERE ..."
            className="border-none bg-transparent px-0 py-2 font-mono text-xs leading-5 shadow-none focus-visible:ring-0"
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
            onChange={setDraft}
            onLinesChange={handleLinesChange}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') {
                return;
              }
              if (event.metaKey || event.ctrlKey) {
                // The page-level shortcut runs the applied query; from inside
                // the box the draft is what the user means, so this claims the
                // event rather than letting it reach the window handler too.
                event.preventDefault();
                event.stopPropagation();
                onRun(draft);
                return;
              }
              // Left alone: a plain Enter breaks the line. It used to be
              // swallowed because the box was one line in a form, where a lone
              // input submits on Enter — a textarea does not, and a statement
              // long enough to need this box is one a reader wants to lay out.
            }}
          />
          <div
            className={cn(
              'flex shrink-0 items-center gap-1',
              multiline ? 'justify-end pt-1' : ''
            )}
          >
            <Button
              type="submit"
              size="sm"
              variant={dirty ? 'active' : 'ghost'}
              disabled={!runnable}
              className={cn(
                'h-6 shrink-0 px-2.5 text-[11px]',
                multiline ? '' : 'my-1.5',
                dirty ? '' : 'text-muted-foreground'
              )}
              title={`Run this query (${getCmdModifierKeySymbol()} + Enter)`}
            >
              Run
            </Button>
            {canSave && onSave !== undefined ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={cn(
                  'h-6 shrink-0 px-2.5 text-[11px]',
                  multiline ? '' : 'my-1.5'
                )}
                title={`Save this query on the dataset (${getCmdModifierKeySymbol()} + S)`}
                onClick={() => onSave()}
              >
                Save
              </Button>
            ) : null}
          </div>
        </div>
        {error !== null ? (
          // Beside the statement that caused it. In the chip row above it sat
          // among sorts and filters with nothing tying it to the query, and
          // truncated at the first interesting part of a DuckDB parse error.
          <p id={errorId} className="text-[11px] leading-snug text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </form>
  );
};
